import type {
  GlobalErrorHandler,
  GlobalMessageHandler,
} from "@/composables/useGlobalHandlers";

/**
 * Reusable copy to clipboard utility with fallback support.
 * Extracted from ReferralLinkCard.vue for project-wide use.
 */

/**
 * Copy text to clipboard with modern API and fallback.
 *
 * @param textToCopy - The text to copy to clipboard
 * @param successMessage - Message to show on successful copy
 * @param globalSuccessHandler - Handler function to show success messages (title, description)
 * @param globalErrorHandler - Handler function to show error messages
 * @param fallbackMessage - Message to show when using fallback method (optional)
 * @param errorMessage - Message to show on copy failure (optional)
 * @throws {Error} When copy operation fails in all attempts
 */
export const copyToClipboard = async (
  textToCopy: string,
  successMessage: string,
  globalSuccessHandler: GlobalMessageHandler,
  globalErrorHandler: GlobalErrorHandler,
  fallbackMessage?: string,
  errorMessage?: string
): Promise<void> => {
  // Check if the Clipboard API is available and we have focus
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(textToCopy);
      globalSuccessHandler("Copied", successMessage);
      return;
    } catch (clipboardError) {
      console.warn(
        "Modern clipboard API failed, falling back to legacy method:",
        clipboardError
      );
    }
  }

  // Fallback to legacy method
  try {
    await copyToFallback(textToCopy);
    globalSuccessHandler("Copied", fallbackMessage ?? successMessage);
  } catch (fallbackError) {
    const finalErrorMessage =
      errorMessage ?? "Copy failed. Please copy manually.";
    console.error("Copy operation failed:", fallbackError);
    globalErrorHandler(finalErrorMessage);
    throw new Error(finalErrorMessage, { cause: fallbackError });
  }
};

/**
 * Fallback copy method using document.execCommand for older browsers.
 *
 * @param textToCopy - The text to copy
 * @throws {Error} When fallback copy method fails
 */
const copyToFallback = (textToCopy: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const textarea = document.createElement("textarea");
    textarea.value = textToCopy;
    textarea.style.position = "fixed";
    textarea.style.left = "-999999px";
    textarea.style.top = "-999999px";
    textarea.setAttribute("readonly", "");
    textarea.style.opacity = "0";

    document.body.appendChild(textarea);

    try {
      textarea.focus();
      textarea.select();

      // For mobile devices
      textarea.setSelectionRange(0, 99999);

      const successful = document.execCommand("copy");

      if (successful) {
        resolve();
      } else {
        reject(new Error("Document execCommand copy failed"));
      }
    } catch (err) {
      console.error("Fallback: Unable to copy", err);
      reject(new Error("Fallback copy method failed"));
    } finally {
      document.body.removeChild(textarea);
    }
  });
};
